/**
 * Right pane: one count, in whatever state it is in.
 *
 *   pending   working mode. Sticky summary, per-line decisions, and either
 *             "Approve and apply" (clean) or "Review N flags to approve"
 *             (flagged). There is no Approve button on a flagged count.
 *   triage    the flag-by-flag step-through (see `FlagTriage`).
 *   waiting   a question is out with the other side. Nothing applies; the
 *             activity trail says what was asked and when.
 *   applied /
 *   rejected  the figures, read only, with what happened at the top.
 *
 * The pane is a flex column, not a scroller: the summary is pinned above the
 * table and the decision actions are pinned below it, so neither the totals
 * nor the approve button can be scrolled out of reach on a 900-line count.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Check,
  ClipboardList,
  Clock,
  CloudUpload,
  Flag,
  Loader2,
  PackageSearch,
  RotateCcw,
  TriangleAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { ClientOrgBadge } from '@/components/ClientOrgBadge';
import { useDateFnsLocale } from '@/lib/format-dates';
import type { StocktakeLineDecision, StocktakeProposalDetail } from '@threadheads/shared';
import { readFlagMeta, type FlagCard } from '../lib/flag-cards';
import { formatDate, formatDateTime, formatQty } from '../lib/format';
import { MessageBody } from './MessageBody';
import { StocktakeFlagline, StocktakeSummaryTiles } from './StocktakeSummaryTiles';
import { StocktakeStatusPill } from './StocktakeStatusPill';
import { VarianceTable, type VarianceTableHandle } from './VarianceTable';

interface StocktakeDetailProps {
  proposal: StocktakeProposalDetail | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  counterpartyName: string;
  cards: FlagCard[];
  /** Client org + Admin/Coordinator: the server's own gate, mirrored. */
  canAct: boolean;
  isClientOrg: boolean;
  showBack: boolean;
  onBack: () => void;
  /** Message mode: leading checkbox column + attachment selection. */
  pickable: boolean;
  selectedSkus: ReadonlySet<string>;
  onToggleSku: (sku: string) => void;
  highlightedSkus: ReadonlySet<string>;
  focusedSku: string | null;
  focusNonce: number;
  varianceRef: React.Ref<VarianceTableHandle>;
  onDecision: (lineId: string, decision: StocktakeLineDecision) => void;
  decisionsPending: boolean;
  onApprove: () => void;
  onReject: () => void;
  onReviewFlags: () => void;
  onResumeReview: () => void;
  /** Re-run the Shopify push for the SKUs that did not make it. */
  onRetryPush: () => void;
  retryPushPending: boolean;
  isMutating: boolean;
  /**
   * Our own messages on this count's thread, oldest first. The waiting-state
   * banner and activity trail are built from these as well as from per-line
   * flag stamps: asking about the UNCOUNTED card resolves no lines (blank
   * lines carry no flag), so a flag-only reading says "0 questions sent" the
   * instant after a question goes out.
   */
  ownMessages: Array<{ id: string; body: string; createdAt: string | null }>;
  /** Rendered instead of everything else while the triage step is open. */
  triageNode: React.ReactNode;
}

export function StocktakeDetail({
  proposal,
  isLoading,
  isError,
  onRetry,
  counterpartyName,
  cards,
  canAct,
  isClientOrg,
  showBack,
  onBack,
  pickable,
  selectedSkus,
  onToggleSku,
  highlightedSkus,
  focusedSku,
  focusNonce,
  varianceRef,
  onDecision,
  decisionsPending,
  onApprove,
  onReject,
  onReviewFlags,
  onResumeReview,
  onRetryPush,
  retryPushPending,
  isMutating,
  ownMessages,
  triageNode,
}: StocktakeDetailProps) {
  const { t } = useTranslation('inventory');
  const dateLocale = useDateFnsLocale();

  // SKU/title filtering is the TABLE's, not the page action bar's: that box
  // narrows the session list, and one input cannot mean two things at once.
  const [lineFilter, setLineFilter] = useState('');
  const proposalId = proposal?.proposalId ?? null;
  useEffect(() => {
    setLineFilter('');
  }, [proposalId]);

  const filteredLines = useMemo(() => {
    if (!proposal) return [];
    const needle = lineFilter.trim().toLowerCase();
    if (!needle) return proposal.lines;
    return proposal.lines.filter(
      (line) =>
        line.sku?.toLowerCase().includes(needle) ||
        line.title?.toLowerCase().includes(needle),
    );
  }, [proposal, lineFilter]);

  // Leaving triage (Cancel, or the last flag cleared then Back) unmounts the
  // panel that owned focus. Hand it to the action that put the reviewer there.
  const primaryActionRef = useRef<HTMLButtonElement>(null);
  const triageOpen = !!triageNode;
  const wasTriageOpen = useRef(triageOpen);
  useEffect(() => {
    if (wasTriageOpen.current && !triageOpen) primaryActionRef.current?.focus();
    wasTriageOpen.current = triageOpen;
  }, [triageOpen]);

  const decisionCounts = useMemo(() => {
    const counts = { accept: 0, recount: 0, reject: 0 };
    for (const line of proposal?.lines ?? []) counts[line.decision] += 1;
    return counts;
  }, [proposal]);

  const flagAskedEvents = useMemo(
    () =>
      cards
        .map((card) => {
          const resolved = card.lines[0] ? readFlagMeta(card.lines[0].flagMeta).resolved : undefined;
          if (resolved?.resolution !== 'asked') return null;
          return {
            key: card.key,
            at: resolved.at ?? null,
            skus: card.lines.map((line) => line.sku).filter(Boolean).join(', '),
          };
        })
        .filter((event): event is { key: string; at: string | null; skus: string } => event != null),
    [cards],
  );

  /**
   * The trail merges both sources: the flag stamps name the SKUs a question
   * was raised against, the thread carries the question that actually went
   * out (including the uncounted card's, which stamps nothing).
   */
  const activityEvents = useMemo(() => {
    const events: Array<
      | { kind: 'asked'; key: string; at: string | null; skus: string }
      | { kind: 'message'; key: string; at: string | null; body: string }
    > = [
      ...flagAskedEvents.map((event) => ({ ...event, kind: 'asked' as const, key: `flag:${event.key}` })),
      ...ownMessages.map((message) => ({
        kind: 'message' as const,
        key: `msg:${message.id}`,
        at: message.createdAt,
        body: message.body,
      })),
    ];
    // ISO timestamps sort lexicographically; undated entries sink to the end.
    return events.sort((a, b) => {
      if (a.at === b.at) return 0;
      if (a.at == null) return 1;
      if (b.at == null) return -1;
      return a.at.localeCompare(b.at);
    });
  }, [flagAskedEvents, ownMessages]);

  /** Never reads 0 in a waiting state a question put us in. */
  const askedCount = Math.max(ownMessages.length, flagAskedEvents.length);

  if (isLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="min-h-0 flex-1 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <EmptyState
          icon={PackageSearch}
          title={t('stocktakes.detail.errorTitle')}
          description={t('stocktakes.detail.errorDescription')}
          action={
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t('stocktakes.empty.error.action')}
            </Button>
          }
        />
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="p-6">
        <EmptyState
          icon={ClipboardList}
          title={t('stocktakes.detail.noSelectionTitle')}
          description={t('stocktakes.detail.noSelectionDescription')}
        />
      </div>
    );
  }

  // What is still OPEN, not what tripped a rule at count time: a count whose
  // flags have all been triaged approves in one click, and so does a count
  // whose only oddity is uncounted lines (blank is never written, so there is
  // nothing to decide). `flagsCount` keeps feeding the Flagged tile.
  const needsReview = proposal.unresolvedFlagsCount > 0;
  const countedOn = formatDate(
    proposal.countedAt ?? proposal.submittedAt ?? proposal.createdAt,
    dateLocale,
  );
  // "3 flags need a look. Mirror pair TEE-XS-BK against TEE-2X-BK, 1 steep
  // drop, 31 SKUs not counted." — built from the UNRESOLVED cards so it never
  // names a flag the reviewer has already cleared, and never claims a flag
  // family the count does not have. Says FLAGS, not lines: the number is the
  // card count "Review N flags" also uses, and one mirror card covers two
  // lines. The uncounted clause is informational and always shown when there
  // are blanks: it gates nothing, but it is the fact most worth naming.
  const flagDetail = (() => {
    const parts: string[] = [];
    const open = cards.filter((card) => !card.resolved);
    for (const card of open) {
      if (card.kind !== 'mirror') continue;
      parts.push(
        t('stocktakes.detail.flagMirror', {
          skuA: card.lines[0]?.sku ?? '',
          skuB: card.lines[1]?.sku ?? '',
        }),
      );
    }
    const drops = open.filter((card) => card.kind === 'drop').length;
    if (drops > 0) parts.push(t('stocktakes.detail.flagDrop', { count: drops }));
    if (proposal.notCountedCount > 0) {
      parts.push(t('stocktakes.detail.flagUncounted', { count: proposal.notCountedCount }));
    }
    return parts.join(', ');
  })();

  // Post-approve Shopify push. `pushSummary` is null while the sweep runs (it
  // is fire-and-forget off approve), so null on an applied count means "still
  // going", never "nothing happened". `failed` is hard errors and `contention`
  // is SKUs still locked after every retry — `retryPush` treats them the same,
  // so the operator-facing number is their sum.
  const push = proposal.pushSummary;
  const pushInFlight = proposal.status === 'applied' && push == null;
  const pushNotReached = push ? push.failed + push.contention : 0;
  const pushClean = push != null && pushNotReached === 0;

  const mode =
    proposal.status === 'pending' && canAct
      ? 'lines'
      : proposal.status === 'applied' ||
          proposal.status === 'approved' ||
          proposal.status === 'rejected'
        ? 'plain'
        : 'flags';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex shrink-0 flex-wrap items-center gap-2.5 border-b px-4 py-3">
        {showBack ? (
          <Button variant="outline" size="sm" className="h-6 gap-1 px-2 text-[11px]" onClick={onBack}>
            <ArrowLeft className="h-3 w-3" aria-hidden="true" />
            {t('stocktakes.detail.backToList')}
          </Button>
        ) : null}
        <StocktakeStatusPill status={proposal.status} />
        <b className="text-sm font-semibold">
          {t('stocktakes.detail.heading', {
            location: proposal.locationName ?? t('stocktakes.row.unknownLocation'),
            count: proposal.skuCount,
            skus: proposal.skuCount.toLocaleString(),
          })}
        </b>
        <span className="text-[11.5px] text-muted-foreground">
          {proposal.countedByName
            ? t('stocktakes.detail.countedOnBy', { date: countedOn, name: proposal.countedByName })
            : t('stocktakes.detail.countedOn', { date: countedOn })}
        </span>
        <div className="flex-1" />
        <ClientOrgBadge
          orgName={counterpartyName}
          srLabel={t('stocktakes.badge.countAria', { org: counterpartyName })}
        />
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
        {triageNode ?? (
          <>
            <div className="shrink-0">
              {proposal.status === 'pending' ? (
                needsReview ? (
                  <StocktakeFlagline tone="warn" icon={<Flag className="h-3.5 w-3.5" />}>
                    <b className="font-semibold">
                      {t('stocktakes.detail.flaglineFlagged', {
                        count: proposal.unresolvedFlagsCount,
                      })}
                    </b>{' '}
                    {flagDetail}
                  </StocktakeFlagline>
                ) : (
                  <StocktakeFlagline tone="ok" icon={<Check className="h-3.5 w-3.5" />}>
                    <b className="font-semibold">{t('stocktakes.detail.flaglineCleanTitle')}</b>{' '}
                    {/* A clean count with blanks in it is still one click, but
                        saying only "approve in one click" would bury the 31
                        rows that stay exactly as they are. */}
                    {proposal.notCountedCount > 0
                      ? t('stocktakes.detail.flaglineCleanBodyUncounted', {
                          count: proposal.notCountedCount,
                        })
                      : t('stocktakes.detail.flaglineCleanBody')}
                  </StocktakeFlagline>
                )
              ) : proposal.status === 'approved' ? (
                // The mid-apply claim state. No actions anywhere, and it never
                // claims to be applied: if the finalize failed this row stays
                // here, and the copy has to stay true in that case too.
                <StocktakeFlagline
                  tone="wait"
                  icon={<Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" />}
                >
                  <b className="font-semibold">{t('stocktakes.detail.flaglineApplyingTitle')}</b>{' '}
                  {t('stocktakes.detail.flaglineApplyingBody')}
                </StocktakeFlagline>
              ) : proposal.status === 'waiting_on_3pl' ? (
                <StocktakeFlagline tone="wait" icon={<Clock className="h-3.5 w-3.5" />}>
                  <b className="font-semibold">
                    {t('stocktakes.detail.flaglineWaitTitle', { org: counterpartyName })}
                  </b>{' '}
                  {askedCount > 0
                    ? t('stocktakes.detail.flaglineWaitBody', { count: askedCount })
                    : t('stocktakes.detail.flaglineWaitBodyPending')}
                </StocktakeFlagline>
              ) : proposal.status === 'rejected' ? (
                <StocktakeFlagline tone="wait" icon={<RotateCcw className="h-3.5 w-3.5" />}>
                  <b className="font-semibold">{t('stocktakes.detail.flaglineRejectedTitle')}</b>{' '}
                  {t('stocktakes.detail.flaglineRejectedBody')}
                </StocktakeFlagline>
              ) : (
                <>
                  <StocktakeFlagline tone="ok" icon={<Check className="h-3.5 w-3.5" />}>
                    <b className="font-semibold">{t('stocktakes.detail.flaglineAppliedTitle')}</b>{' '}
                    {/* A finished, clean push folds into this one line instead
                        of earning a banner of its own: nothing to act on. */}
                    {pushClean
                      ? t('stocktakes.detail.flaglineAppliedBodyPushed', { org: counterpartyName })
                      : t('stocktakes.detail.flaglineAppliedBody', { org: counterpartyName })}
                  </StocktakeFlagline>

                  {pushInFlight ? (
                    <p
                      role="status"
                      className="mb-2 flex items-center gap-1.5 text-[11.5px] text-muted-foreground"
                    >
                      <Loader2
                        className="h-3 w-3 shrink-0 motion-safe:animate-spin"
                        aria-hidden="true"
                      />
                      {t('stocktakes.detail.pushInFlight')}
                    </p>
                  ) : null}

                  {push != null && pushNotReached > 0 ? (
                    <div
                      role="status"
                      className="mb-2 flex flex-wrap items-center gap-2 rounded-md bg-amber-100 px-2.5 py-1.5 text-[11.5px] text-amber-800 ring-1 ring-amber-300/80"
                    >
                      <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      <span className="min-w-0 tabular-nums">
                        <b className="font-semibold">
                          {t('stocktakes.detail.pushFailed', {
                            failed: formatQty(pushNotReached),
                            total: formatQty(push.total),
                          })}
                        </b>{' '}
                        {t('stocktakes.detail.pushFailedBody')}
                      </span>
                      <div className="flex-1" />
                      {/* It is the CLIENT's Shopify store, and `retryPush` is
                          client-org-only on the server. The owner still sees
                          the banner (it is their count that did not land), it
                          just carries no button they would be 403'd on. */}
                      {canAct ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 gap-1.5 border-amber-300 bg-transparent px-2.5 text-[11px] text-amber-800 hover:bg-amber-200 hover:text-amber-900"
                          onClick={onRetryPush}
                          disabled={retryPushPending}
                        >
                          {retryPushPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                          ) : (
                            <CloudUpload className="h-3 w-3" aria-hidden="true" />
                          )}
                          {t('stocktakes.action.retryPush')}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
              <StocktakeSummaryTiles counts={proposal} />
            </div>

            {proposal.status === 'waiting_on_3pl' ? (
              <div className="shrink-0 rounded-lg border bg-card p-3">
                <b className="text-[12.5px] font-semibold">
                  {t('stocktakes.detail.activityTitle')}
                </b>
                {/* Capped: a long back and forth must not push the variance
                    table off screen, and the quoted bodies are previews, not
                    the thread. */}
                <div className="mt-2 max-h-[180px] space-y-2.5 overflow-y-auto border-l-2 border-border pl-3">
                  {activityEvents.map((event) =>
                    event.kind === 'asked' ? (
                      <p key={event.key} className="text-[11.5px] text-muted-foreground">
                        <b className="font-semibold text-foreground">
                          {t('stocktakes.detail.askedEvent', {
                            org: counterpartyName,
                            skus: event.skus,
                          })}
                        </b>
                        {event.at ? `, ${formatDateTime(event.at, dateLocale)}` : ''}
                      </p>
                    ) : (
                      <div key={event.key} className="text-[11.5px] text-muted-foreground">
                        <b className="font-semibold text-foreground">
                          {t('stocktakes.detail.messageEvent', { org: counterpartyName })}
                        </b>
                        {event.at ? `, ${formatDateTime(event.at, dateLocale)}` : ''}
                        {/* A preview, not the thread: clamped on whole lines so
                            a bullet list is cut between dot points rather than
                            through one. The full message is one click away. */}
                        <MessageBody
                          body={event.body}
                          maxLines={3}
                          className="mt-1 rounded-md bg-muted px-2.5 py-2 text-[11.5px] leading-relaxed text-foreground"
                        />
                      </div>
                    ),
                  )}
                  <p className="text-[11.5px] text-muted-foreground">
                    <b className="font-semibold">{t('stocktakes.detail.awaitingReply')}</b>{' '}
                    {t('stocktakes.detail.awaitingReplyBody')}
                  </p>
                </div>
              </div>
            ) : null}

            <VarianceTable
              ref={varianceRef}
              lines={filteredLines}
              totalCount={proposal.lines.length}
              resetKey={`${proposal.proposalId}:${lineFilter}`}
              mode={mode}
              pickable={pickable}
              selectedSkus={selectedSkus}
              onToggleSku={onToggleSku}
              highlightedSkus={highlightedSkus}
              focusedSku={focusedSku}
              focusNonce={focusNonce}
              filterValue={lineFilter}
              onFilterChange={setLineFilter}
              onDecision={onDecision}
              decisionsDisabled={decisionsPending}
            />

            {proposal.status === 'pending' && canAct ? (
              <div className="shrink-0 space-y-2.5">
                <p className="text-[11.5px] text-muted-foreground">
                  <Trans
                    i18nKey="stocktakes.detail.decisionHelp"
                    ns="inventory"
                    components={{ b: <b className="font-semibold text-foreground" /> }}
                  />{' '}
                  {t('stocktakes.detail.decisionCurrent', {
                    accept: decisionCounts.accept,
                    recount: decisionCounts.recount,
                    reject: decisionCounts.reject,
                  })}
                </p>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 border-orange-300 px-3 text-xs text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                    onClick={onReject}
                    disabled={isMutating}
                  >
                    {t('stocktakes.action.rejectWholeCount')}
                  </Button>
                  {needsReview ? (
                    <Button
                      ref={primaryActionRef}
                      variant="outline"
                      size="sm"
                      className="h-8 border-amber-300 bg-amber-100 px-3 text-xs text-amber-800 hover:bg-amber-200 hover:text-amber-900"
                      onClick={onReviewFlags}
                      disabled={isMutating}
                    >
                      {t('stocktakes.action.reviewFlagsToApprove', {
                        count: proposal.unresolvedFlagsCount,
                      })}
                    </Button>
                  ) : (
                    <Button
                      ref={primaryActionRef}
                      size="sm"
                      className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
                      onClick={onApprove}
                      disabled={isMutating}
                    >
                      {isMutating ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                      ) : null}
                      {t('stocktakes.action.approveAndApply')}
                    </Button>
                  )}
                </div>
              </div>
            ) : proposal.status === 'waiting_on_3pl' && canAct ? (
              <div className="flex shrink-0 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs"
                  onClick={onResumeReview}
                  disabled={isMutating}
                >
                  {t('stocktakes.action.resumeReview')}
                </Button>
              </div>
            ) : proposal.status === 'pending' || proposal.status === 'waiting_on_3pl' ? (
              <p className="shrink-0 text-right text-[11.5px] text-muted-foreground">
                {isClientOrg
                  ? t('stocktakes.detail.noPermission')
                  : t('stocktakes.detail.clientDecides', { org: counterpartyName })}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
